<template>
  <div @click.stop="handleSendCode">
    <slot
      :remainingTime="remainingTime"
      :isLoading="isLoading"
      :disabled="disabledResend"
      :buttonText="buttonText"
    >
      <GtcButton v-bind="attrs" :disabled="disabled || disabledResend" :loading="isLoading">
        {{ buttonText }}
      </GtcButton>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, useAttrs, onDeactivated } from 'vue'
import { http } from '@/libs'
import type { Method, RequestParams } from '@/types'
import { v4 as uuidv4 } from 'uuid'
import i18n from '@/locales'
import { GtcButton } from '@/components'
import { onBeforeRouteLeave } from 'vue-router'

const t = (i18n.global as any).t

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    resendTime?: number | string
    apiUrl?: string
    method?: Method
    apiParams?: RequestParams
    instanceId?: string
    targetType?: 'email' | 'phone'
    targetField?: string
    sendFn?: (ctx: { apiUrl: string; method: Method; apiParams: RequestParams }) => Promise<unknown>
  }>(),
  {
    targetType: 'email',
    targetField: 'email',
    disabled: false,
    resendTime: 60,
    method: 'POST',
    apiUrl: '/api/pub/get_code',
  },
)

const emit = defineEmits<{
  (e: 'send-success'): void
  (e: 'send-failed', error: any): void
}>()

const attrs = useAttrs()

// 生成并持久化一个 uuid（仅首次生成）；若父组件未传入 instanceId，则用它作为稳定实例ID
const PERSIST_ID_KEY = 'email_verify_persist_id'
let persistedId = ''
try {
  persistedId = localStorage.getItem(PERSIST_ID_KEY) || ''
  if (!persistedId) {
    persistedId = uuidv4()
    localStorage.setItem(PERSIST_ID_KEY, persistedId)
  }
} catch {
  /* ignore */
}

const instanceId = props.instanceId || persistedId || uuidv4()

// storageKey 基于稳定的 instanceId，刷新后不再变化
const storageKey = `email_verify_nextAllowedAt_${instanceId}`

// 存储下一次可发送的时间戳（毫秒）；null 表示无需冷却
const nextAllowedAt = ref<number | null>(null)
const nowTick = ref(Date.now())
const isLoading = ref(false)
const timer = ref<ReturnType<typeof setInterval> | null>(null)

// 确保 resendTime 始终为数值毫秒，避免 "60" * 1000 等类型问题
const resendMs = computed(() => Math.max(0, Number(props.resendTime ?? 60)) * 1000)

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())

// 将用户输入的手机号做“软规范化”，只保留 + 和数字，去掉空格/连字符/括号/点等分隔符
const normalizePhone = (v: string) => v.trim().replace(/[\s\-().]/g, '')

// 仅接受 E.164（全球通用）：+ 开头，后跟 8-15 位数字
const isValidPhone = (v: string) => /^\+[1-9]\d{6,14}$/.test(normalizePhone(v))
// 由时间点实时计算剩余秒数，而非自减，避免漂移与刷新误差
const remainingTime = computed(() => {
  if (!nextAllowedAt.value) return 0
  const ms = nextAllowedAt.value - nowTick.value
  return ms > 0 ? Math.ceil(ms / 1000) : 0
})

const disabledResend = computed(() => remainingTime.value > 0 || isLoading.value)

const buttonText = computed(() =>
  remainingTime.value === 0
    ? t('components.sendCodeButton.send-code')
    : t('components.sendCodeButton.resend-code', { time: remainingTime.value }),
)

// 定时“心跳”检查是否到点；到点后清理本地存储与状态
const startTimer = () => {
  if (timer.value) clearInterval(timer.value)
  // 先立即打点一次，UI 立刻更新
  nowTick.value = Date.now()
  timer.value = setInterval(() => {
    // 刷新“时钟”，触发 remainingTime 重算
    nowTick.value = Date.now()
    if (remainingTime.value <= 0) {
      if (timer.value) {
        clearInterval(timer.value)
        timer.value = null
      }
      nextAllowedAt.value = null
      try {
        localStorage.removeItem(storageKey)
      } catch {}
    }
  }, 1000)
}

// 清理无效/过期的键，避免长期堆积
function cleanupStaleKeys() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (key.startsWith('email_verify_nextAllowedAt_')) {
        const raw = localStorage.getItem(key)
        const ts = raw ? Number(raw) : NaN
        if (!Number.isFinite(ts) || ts <= Date.now()) {
          localStorage.removeItem(key)
        }
      }
    }
  } catch {}
}

// 监听其他标签页对同一 storageKey 的更新，实现多标签页同步
const onStorage = (e: StorageEvent) => {
  if (e.key !== storageKey) return
  const ts = e.newValue ? Number(e.newValue) : NaN
  nextAllowedAt.value = Number.isFinite(ts) ? ts : null
  if (remainingTime.value > 0) startTimer()
}

const handleSendCode = async () => {
  if (props.disabled) return

  if (remainingTime.value > 0) {
    // 冷却中直接提示剩余秒数
    return window.GtcNotification(
      'warning',
      t('components.sendCodeButton.wait-retry', { timeLeft: remainingTime.value }),
    )
  }
  if (isLoading.value) {
    return window.GtcNotification('warning', t('components.sendCodeButton.request-in-progress'))
  }

  const { apiUrl, method, apiParams, sendFn } = props
  if (!apiUrl && !sendFn) {
    return window.GtcNotification('error', t('components.sendCodeButton.no-api-url'))
  }
  let validatedParams: Record<string, unknown> = apiParams ? { ...(apiParams as any) } : undefined
  if (apiParams) {
    const field = props.targetField || (props.targetType === 'phone' ? 'phone' : 'email')
    const rawTarget = (validatedParams as any)[field]
    const targetVal =
      typeof rawTarget === 'string' ? rawTarget : (attrs?.value as string | undefined)

    if (props.targetType === 'email') {
      // 邮箱格式校验，失败直接中断
      if (!targetVal || !isValidEmail(String(targetVal))) {
        return window.GtcNotification('error', t('components.sendCodeButton.invalid-email'))
      }
      // 将通过校验的值写回
      validatedParams[field] = String(targetVal)
    } else if (props.targetType === 'phone') {
      // 全球号码统一 E.164，先规范化再校验
      console.log(334343, targetVal)
      const normalized = targetVal ? normalizePhone(String(targetVal)) : ''
      if (!normalized || !isValidPhone(normalized)) {
        return window.GtcNotification(
          'error',
          t('components.sendCodeButton.invalid-phone') ||
            '请输入有效的国际手机号（E.164，如 +14155552671 或 +8613812345678）',
        )
      }
      // 将规范化后的 E.164 号码写回
      validatedParams[field] = normalized
    }
  }
  try {
    isLoading.value = true

    // 调用可选的自定义发送函数；否则走默认 http
    sendFn
      ? await sendFn({ apiUrl: apiUrl!, method: method!, apiParams: validatedParams || {} })
      : await sendRequest(method!, apiUrl!, { ...(validatedParams || {}) })

    window.GtcNotification('success', t('components.sendCodeButton.send-success'))

    // 点击成功后立刻进入冷却（先赋值再启动心跳，UI 立即更新）
    nextAllowedAt.value = Date.now() + resendMs.value
    try {
      localStorage.setItem(storageKey, String(nextAllowedAt.value))
    } catch {}
    startTimer()

    emit('send-success')
  } catch (error) {
    console.error('发送验证码请求出错:', error)

    // 失败即取消冷却，限流交给后端兜底
    if (timer.value) {
      clearInterval(timer.value)
      timer.value = null
    }
    nextAllowedAt.value = null
    try {
      localStorage.removeItem(storageKey)
    } catch {}

    // window.GtcNotification(
    //   'error',
    //   error instanceof Error ? error.message : t('components.sendCodeButton.send-failed'),
    // )
    emit('send-failed', error)
  } finally {
    isLoading.value = false
  }
}

function clearCountdownStorage() {
  // 复杂逻辑：确保计时器停掉、状态复位、localStorage 键删除
  if (timer.value) {
    clearInterval(timer.value)
    timer.value = null
  }
  nextAllowedAt.value = null
  try {
    localStorage.removeItem(storageKey)
  } catch {}
}

let onPageHideHandler: (() => void) | null = null

async function sendRequest(method: string, apiUrl: string, params: Record<string, unknown>) {
  if (method.toUpperCase() === 'GET') {
    return http.get(apiUrl, { params })
  }
  return http.post(apiUrl, params)
}

onBeforeRouteLeave(() => {
  clearCountdownStorage()
})

onDeactivated(() => {
  clearCountdownStorage()
})

onMounted(() => {
  cleanupStaleKeys()

  // 从 localStorage 恢复“下一次可发送时间点”
  try {
    const raw = localStorage.getItem(storageKey)
    const ts = raw ? Number(raw) : NaN
    nextAllowedAt.value = Number.isFinite(ts) ? ts : null
  } catch {
    nextAllowedAt.value = null
  }

  // 仍在冷却则启动心跳；否则清理残留键
  if (remainingTime.value > 0) {
    startTimer()
  } else {
    try {
      localStorage.removeItem(storageKey)
    } catch {}
  }

  window.addEventListener('storage', onStorage)
  onPageHideHandler = () => {
    clearCountdownStorage()
  }
  window.addEventListener('pagehide', onPageHideHandler)
  window.addEventListener('beforeunload', onPageHideHandler)
})

onUnmounted(() => {
  clearCountdownStorage()

  window.removeEventListener('storage', onStorage)
  if (onPageHideHandler) {
    window.removeEventListener('pagehide', onPageHideHandler)
    window.removeEventListener('beforeunload', onPageHideHandler)
    onPageHideHandler = null
  }
})
</script>
